import { useInfiniteQuery } from 'react-query';
import {
  FieldAuditLog,
  FieldAuditLogRaw,
  FieldAuditLogResponse,
  Field,
} from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { fetcher, ServerErrorResponse, performanceReportingApiUrl, createParamString } from 'data_sources/constants';

const parseAuditLogState = (stateString: string | null): Field | null => {
  if (!stateString) {
    return null;
  }
  try {
    return JSON.parse(stateString) as Field;
  } catch (error) {
    console.error('Error parsing audit log state:', error);
    return null;
  }
};

// Fetch field audit log from API
const getFieldAuditLog = async (
  fieldId: number,
  params: Record<string, any> = {},
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<FieldAuditLogResponse> => {
  try {
    const baseUrl = `${performanceReportingApiUrl()}/performance/fields/v1/audit-logs`;
    const queryParams = createParamString({
      id: fieldId,
      entity: 'Field',
      ...params,
    });
    const url = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;

    const resp = await fetcher({
      method: 'get',
      url,
      options: { signal },
    });

    const rawResponse = resp as { content: FieldAuditLogRaw[]; count: number; nextToken: string | null };
    const parsedContent: FieldAuditLog[] = rawResponse.content.map((rawLog) => ({
      ...rawLog,
      previousState: parseAuditLogState(rawLog.previousState),
      newState: parseAuditLogState(rawLog.newState) as Field,
    }));

    return {
      content: parsedContent,
      count: rawResponse.count,
      nextToken: rawResponse.nextToken,
    };
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export const useFieldAuditLog = (fieldId: number, options = {}, onError?: (errors: ServerErrorResponse) => void) => {
  return useInfiniteQuery<FieldAuditLogResponse, ServerErrorResponse>(
    ['fieldAuditLog', fieldId],
    async ({ pageParam, signal }) => {
      const combinedParams = {
        nextToken: pageParam,
      };
      return await getFieldAuditLog(fieldId, combinedParams, signal, onError);
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
      enabled: !!fieldId,
      ...options,
    }
  );
};

export { getFieldAuditLog };
